'use client'

import * as React from 'react'
import { cn } from '../../utils/cn'
import { ArrowRight } from 'lucide-react'

interface HeroBadge {
  text: string
  href?: string
}

interface HeroCTA {
  text: string
  href: string
}

interface HeroSplitProps {
  badge?: HeroBadge
  headline: string
  subheadline: string
  primaryCTA: HeroCTA
  secondaryCTA?: HeroCTA
  media: React.ReactNode
  reverse?: boolean
}

export function HeroSplit({
  badge,
  headline,
  subheadline,
  primaryCTA,
  secondaryCTA,
  media,
  reverse = false
}: HeroSplitProps) {
  return (
    <section className="relative min-h-[80vh] flex items-center pt-24 pb-16">
      {/* Subtle gradient background */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background: reverse
            ? `radial-gradient(ellipse 50% 80% at 20% 50%, var(--accent-glow), transparent)`
            : `radial-gradient(ellipse 50% 80% at 80% 50%, var(--accent-glow), transparent)`
        }}
      />

      <div className="landing-container relative z-10">
        <div className={cn(
          'grid lg:grid-cols-2 gap-12 lg:gap-16 items-center',
          reverse && 'lg:[&>*:first-child]:order-2'
        )}>
          {/* Content */}
          <div className="flex flex-col">
            {/* Badge */}
            {badge && (
              <a
                href={badge.href}
                className={cn(
                  'inline-flex items-center gap-2 px-3 py-1 mb-6 w-fit',
                  'text-sm font-medium',
                  'bg-[var(--landing-bg-card)] border border-[var(--landing-border)]',
                  'rounded-full',
                  'text-[var(--landing-text-secondary)]',
                  'hover:border-[var(--landing-border-strong)]',
                  'transition-all duration-200'
                )}
              >
                {badge.text}
                <ArrowRight className="h-3 w-3" />
              </a>
            )}

            {/* Headline */}
            <h1 className="headline-lg mb-6">
              {headline}
            </h1>

            {/* Subheadline */}
            <p className="body-lg mb-8 max-w-lg">
              {subheadline}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <a
                href={primaryCTA.href}
                className={cn(
                  'inline-flex items-center justify-center gap-2',
                  'px-6 py-3 text-base font-medium rounded-lg',
                  'bg-[var(--accent)] text-white',
                  'hover:bg-[var(--accent-hover)]',
                  'transition-all duration-200',
                  'cta-glow'
                )}
              >
                {primaryCTA.text}
                <ArrowRight className="h-4 w-4" />
              </a>
              {secondaryCTA && (
                <a
                  href={secondaryCTA.href}
                  className={cn(
                    'inline-flex items-center justify-center gap-2',
                    'px-6 py-3 text-base font-medium rounded-lg',
                    'bg-transparent border border-[var(--landing-border)]',
                    'text-[var(--landing-text-primary)]',
                    'hover:border-[var(--landing-border-strong)] hover:bg-[var(--landing-bg-hover)]',
                    'transition-all duration-200'
                  )}
                >
                  {secondaryCTA.text}
                </a>
              )}
            </div>
          </div>

          {/* Media */}
          <div className="relative">
            {media}
          </div>
        </div>
      </div>
    </section>
  )
}
